import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Bell, Plus, Search } from "lucide-react";
import { useStore } from "../lib/store";
import { unreadCount } from "../features/notifications/feed";
import { NewLeadDrawer } from "../features/leads/NewLeadDrawer";
import { ToastHost } from "../ui/ToastHost";
import { BrandMark } from "./Brand";
import { Starfield } from "./Starfield";
import { Meteors } from "./Meteors";
import { GlobalSearch } from "./GlobalSearch";
import { AccountMenu } from "./AccountMenu";
import { NoticePopover } from "./NoticePopover";
import { ThemeToggle } from "./ThemeToggle";
import { useProfileIdentity } from "./useProfileIdentity";
import { PRIMARY_NAV, appPath } from "./routes";
import "../styles/index.css";
import "./shell.css";

/**
 * Shell do Frontend V2 — a topbar do protótipo Zênite: marca, navegação em
 * pílulas com os pares Leads↔Clientes e Mensagens↔Inbox, relógio, busca,
 * sino com popover, alternância de tema, "Novo lead" e a conta.
 *
 * O escopo `data-app="v2"` do CSS é ligado no bootstrap (`main.tsx`), não aqui:
 * a espera da sessão renderiza antes de qualquer superfície e também precisa
 * dos tokens.
 */
const TIME_ZONE = "America/Sao_Paulo";

const timeFormat = new Intl.DateTimeFormat("pt-BR", {
  hour: "2-digit",
  minute: "2-digit",
  timeZone: TIME_ZONE,
});

const dateFormat = new Intl.DateTimeFormat("pt-BR", {
  weekday: "short",
  day: "2-digit",
  month: "short",
  timeZone: TIME_ZONE,
});

function Clock() {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <div className="clock" aria-hidden="true">
      <span className="c-time num">{timeFormat.format(now)}</span>
      <span className="c-date">{dateFormat.format(now).replace(/\.$/, "")}</span>
    </div>
  );
}

/**
 * Sino com o que está em aberto.
 *
 * O número vem do mesmo feed derivado da central: não existe contador
 * guardado em outro lugar para divergir do que a página mostra (AGENTS §10).
 */
function NoticeBell() {
  const store = useStore();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);
  const unread = unreadCount(store.noticeFeed());
  const label =
    unread === 0
      ? "Notificações — nenhuma não lida"
      : `Notificações — ${unread} ${unread === 1 ? "não lida" : "não lidas"}`;

  return (
    <>
      <button
        ref={ref}
        type="button"
        className="zn-icon-btn"
        aria-label={label}
        title={label}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <Bell size={17} aria-hidden="true" />
        {unread > 0 && (
          <span className="badge" aria-hidden="true">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      <NoticePopover open={open} onClose={() => setOpen(false)} anchorRef={ref} />
    </>
  );
}

function isUnder(pathname: string, ...relatives: string[]): boolean {
  return relatives.some((relative) => {
    const base = appPath(relative);
    return pathname === base || pathname.startsWith(`${base}/`);
  });
}

/**
 * Par com revelação do protótipo: a segunda entrada só aparece (e só entra
 * na ordem do teclado) enquanto a primeira ou ela mesma está ativa.
 */
function RevealPair({
  className,
  expanded,
  primary,
  secondary,
}: {
  className: string;
  expanded: boolean;
  primary: ReactNode;
  secondary: ReactNode;
}) {
  return (
    <span className={expanded ? `${className} expanded` : className}>
      {primary}
      <span className="z-client-reveal">
        <span>{secondary}</span>
      </span>
    </span>
  );
}

function PillNav() {
  const location = useLocation();
  const store = useStore();
  const navRef = useRef<HTMLElement>(null);
  const indicatorRef = useRef<HTMLSpanElement>(null);

  // No perfil, o par só abre quando o cadastro é cliente — é de onde a pessoa
  // veio e para onde volta. Num lead em negociação, "Clientes" não pertence à
  // barra, e o protótipo a mantém recolhida.
  const profileId = location.pathname.startsWith(`${appPath("leads")}/`)
    ? location.pathname.slice(`${appPath("leads")}/`.length).split("/")[0]
    : "";
  const profileIsClient = profileId
    ? Boolean(store.leads.find((lead) => lead.id === profileId)?.clientSince)
    : false;
  const leadsExpanded =
    isUnder(location.pathname, "clientes", "lixeira") ||
    (isUnder(location.pathname, "leads") && (!profileId || profileIsClient));
  const trashActive = isUnder(location.pathname, "lixeira");
  const messagesExpanded = isUnder(location.pathname, "mensagens");
  const inboxActive = location.pathname === appPath("mensagens") && location.search.includes("canal=email");

  /**
   * Indicador do protótipo (`moveIndicator`): a pílula azul viaja por
   * requestAnimationFrame durante 360ms, lendo A CADA QUADRO a posição atual
   * do destino. Isso importa porque abrir o par Leads/Clientes muda a largura
   * da barra e desloca tudo enquanto a viagem acontece; uma transição CSS
   * para um alvo medido uma vez chegaria no lugar errado e saltaria depois.
   * Movimento reduzido: pula direto para o destino.
   */
  useLayoutEffect(() => {
    const nav = navRef.current;
    const ind = indicatorRef.current;
    if (!nav || !ind) return;
    let frame = 0;

    const move = () => {
      cancelAnimationFrame(frame);
      const active = nav.querySelector<HTMLElement>(".active");
      if (!active) {
        ind.hidden = true;
        return;
      }
      const from = ind.hidden ? null : ind.getBoundingClientRect();
      const start = performance.now();
      const reduced =
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      ind.hidden = false;
      const step = (now: number) => {
        if (!active.isConnected) return;
        const target = active.getBoundingClientRect();
        const container = nav.getBoundingClientRect();
        const t = reduced || !from?.width ? 1 : Math.min(1, (now - start) / 360);
        const ease = 1 - Math.pow(1 - t, 3);
        const left = from?.width ? from.left + (target.left - from.left) * ease : target.left;
        const width = from?.width ? from.width + (target.width - from.width) * ease : target.width;
        ind.style.left = `${left - container.left - nav.clientLeft}px`;
        ind.style.width = `${width}px`;
        if (t < 1) frame = requestAnimationFrame(step);
      };
      frame = requestAnimationFrame(step);
    };

    move();
    let alive = true;
    window.addEventListener("resize", move);
    void document.fonts?.ready.then(() => {
      if (alive) move();
    });
    return () => {
      alive = false;
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", move);
    };
  }, [location.pathname, location.search, leadsExpanded, messagesExpanded]);

  const linkClass = ({ isActive }: { isActive: boolean }) => (isActive ? "active" : "");

  return (
    <nav ref={navRef} className="pillnav" aria-label="Navegação principal">
      <span ref={indicatorRef} className="indicator" aria-hidden="true" hidden />
      {PRIMARY_NAV.map((entry) => {
        if (entry.path === "leads") {
          return (
            <RevealPair
              key={entry.path}
              className="z-leads-pair"
              expanded={leadsExpanded}
              primary={
                // A lixeira é um recorte de Leads, como no protótipo: a pílula
                // de Leads continua acesa enquanto ela está aberta.
                <NavLink
                  to={appPath("leads")}
                  className={({ isActive }) => (isActive || trashActive ? "active" : "")}
                  aria-current={trashActive ? "page" : undefined}
                >
                  {entry.label}
                </NavLink>
              }
              secondary={
                <NavLink
                  to={appPath("clientes")}
                  className={linkClass}
                  tabIndex={leadsExpanded ? 0 : -1}
                  aria-hidden={leadsExpanded ? undefined : "true"}
                >
                  Clientes
                </NavLink>
              }
            />
          );
        }
        if (entry.path === "mensagens") {
          return (
            <RevealPair
              key={entry.path}
              className="z-messages-pair"
              expanded={messagesExpanded}
              primary={
                <NavLink
                  to={appPath("mensagens")}
                  className={({ isActive }) => (isActive && !inboxActive ? "active" : "")}
                  aria-current={messagesExpanded && !inboxActive ? "page" : undefined}
                >
                  {entry.label}
                </NavLink>
              }
              secondary={
                <Link
                  to={`${appPath("mensagens")}?canal=email`}
                  className={inboxActive ? "active" : ""}
                  aria-current={inboxActive ? "page" : undefined}
                  tabIndex={messagesExpanded ? 0 : -1}
                  aria-hidden={messagesExpanded ? undefined : "true"}
                >
                  Inbox
                </Link>
              }
            />
          );
        }
        return (
          <NavLink key={entry.path} to={appPath(entry.path)} end={entry.path === ""} className={linkClass}>
            {entry.label}
          </NavLink>
        );
      })}
    </nav>
  );
}

export function AppShell() {
  const identity = useProfileIdentity();
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  // Atalho da busca global, como no protótipo. Ignora digitação em campos.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== "k") return;
      event.preventDefault();
      setSearchOpen(true);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <div className="cx-app">
      {/* Céu do protótipo: estrelas em deriva e três meteoros, atrás de tudo. */}
      <Starfield className="zn-stars" />
      <Meteors />
      {/* Brilho compartilhado dos gráficos e avatares (filtro do protótipo). */}
      <svg width="0" height="0" aria-hidden="true" focusable="false" style={{ position: "absolute", pointerEvents: "none" }}>
        <defs>
          <filter id="zenite-glow" x="-50%" y="-50%" width="200%" height="200%" colorInterpolationFilters="sRGB">
            <feGaussianBlur in="SourceGraphic" stdDeviation="2.4" result="brilho" />
            <feComponentTransfer in="brilho" result="halo">
              <feFuncA type="linear" slope=".7" />
            </feComponentTransfer>
            <feMerge>
              <feMergeNode in="halo" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
      </svg>
      <a className="cx-skip-link" href="#cx-conteudo">
        Pular para o conteúdo
      </a>

      <header className="zn-topbar">
        <Link className="zn-brand" to={appPath()} aria-label="CosmosLeads — início">
          <BrandMark />
          <span className="brand-name">
            <b>Cosmos</b>
            <span>Leads</span>
          </span>
        </Link>

        <PillNav />

        <div className="top-actions">
          <Clock />
          <button
            type="button"
            className="zn-icon-btn"
            aria-label="Buscar (Ctrl+K)"
            title="Buscar (Ctrl+K)"
            onClick={() => setSearchOpen(true)}
          >
            <Search size={17} aria-hidden="true" />
          </button>
          <NoticeBell />
          <ThemeToggle />
          <button type="button" className="btn-primary" onClick={() => setCreating(true)}>
            <Plus size={15} strokeWidth={2.4} aria-hidden="true" />
            <span>Novo lead</span>
          </button>
          <AccountMenu name={identity.name} email={identity.email} />
        </div>
      </header>

      <main id="cx-conteudo" className="cx-main" tabIndex={-1}>
        <Outlet />
      </main>

      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
      {creating && (
        <NewLeadDrawer
          onClose={() => setCreating(false)}
          onCreated={(leadId) => {
            setCreating(false);
            navigate(`${appPath("leads")}/${leadId}`);
          }}
        />
      )}
      <ToastHost />
    </div>
  );
}
